import React, { useRef, useEffect, useState, useCallback } from 'react';
import * as d3 from 'd3';

const MARGIN = { top: 40, right: 30, bottom: 30, left: 30 };
const HEIGHT = 380;

const DIMENSIONS = [
  { key: 'pest365', label: 'Pest365' },
  { key: 'pest365Plus', label: 'Pest365+' },
  { key: 'oneTime', label: 'One-Time' },
  { key: 'initialServiceVisit', label: 'Init. Visit' },
  { key: 'mosquitoTickControl', label: 'Mosquito/Tick' },
];

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '4px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '16px',
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '12px',
    flexWrap: 'wrap',
  },
  selectWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
    color: '#6b7280',
  },
  select: {
    padding: '4px 8px',
    fontSize: '12px',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    background: '#fff',
    color: '#374151',
    outline: 'none',
    cursor: 'pointer',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  pills: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
  },
  pill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    padding: '3px 10px',
    fontSize: '11px',
    fontWeight: 500,
    borderRadius: '12px',
    border: '1px solid #e5e7eb',
    cursor: 'pointer',
    userSelect: 'none',
    transition: 'opacity 0.15s ease',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  pillDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
  },
  container: {
    overflowX: 'auto',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
};

export default function ParallelCoordinates({ data }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const brushesRef = useRef({});
  const [tooltip, setTooltip] = useState(null);
  const [colorBy, setColorBy] = useState('state');
  const [hiddenStates, setHiddenStates] = useState(new Set());
  const [brushExtents, setBrushExtents] = useState({});

  const toggleState = useCallback((state) => {
    setHiddenStates((prev) => {
      const next = new Set(prev);
      if (next.has(state)) {
        next.delete(state);
      } else {
        next.add(state);
      }
      return next;
    });
  }, []);

  // Derive unique states and color mapping
  const states = React.useMemo(() => {
    if (!data || data.length === 0) return [];
    const unique = [...new Set(data.map((d) => d.state).filter(Boolean))].sort();
    return unique;
  }, [data]);

  const stateColorScale = React.useMemo(() => {
    return d3.scaleOrdinal().domain(states).range(d3.schemeTableau10);
  }, [states]);

  useEffect(() => {
    if (!data || data.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();
    brushesRef.current = {};

    const containerWidth = containerRef.current
      ? containerRef.current.clientWidth
      : 700;
    const width = Math.max(500, containerWidth);
    const innerW = width - MARGIN.left - MARGIN.right;
    const innerH = HEIGHT - MARGIN.top - MARGIN.bottom;

    svg.attr('width', width).attr('height', HEIGHT);

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    // Build Y scales for each dimension (independent min/max)
    const yScales = {};
    DIMENSIONS.forEach((dim) => {
      const values = data
        .filter((d) => d[dim.key] != null && typeof d[dim.key] === 'number')
        .map((d) => d[dim.key]);
      const extent = values.length > 0 ? d3.extent(values) : [0, 100];
      const padding = (extent[1] - extent[0]) * 0.05 || 5;
      yScales[dim.key] = d3.scaleLinear()
        .domain([extent[0] - padding, extent[1] + padding])
        .range([innerH, 0]);
    });

    // X scale: evenly spaced axes
    const xScale = d3.scalePoint()
      .domain(DIMENSIONS.map((d) => d.key))
      .range([0, innerW]);

    // Filter visible data
    const visibleData = data.filter((d) => !hiddenStates.has(d.state));

    // Line generator: skip null values
    const lineGen = d3.line().curve(d3.curveMonotoneX);

    // Determine if a row passes all active brushes
    function isWithinBrushes(row) {
      for (const dimKey of Object.keys(brushExtents)) {
        const [y0, y1] = brushExtents[dimKey]; // pixel coords (top, bottom)
        const val = row[dimKey];
        if (val == null || typeof val !== 'number') return false;
        const yPos = yScales[dimKey](val);
        if (yPos < y0 || yPos > y1) return false;
      }
      return true;
    }

    // Draw lines
    const linesG = g.append('g').attr('class', 'lines');

    visibleData.forEach((row) => {
      const points = [];
      DIMENSIONS.forEach((dim) => {
        const val = row[dim.key];
        if (val != null && typeof val === 'number') {
          points.push([xScale(dim.key), yScales[dim.key](val)]);
        }
      });

      if (points.length < 2) return;

      const withinBrush = isWithinBrushes(row);
      const hasBrushes = Object.keys(brushExtents).length > 0;

      linesG
        .append('path')
        .datum(row)
        .attr('d', lineGen(points))
        .attr('fill', 'none')
        .attr('stroke', stateColorScale(row.state))
        .attr('stroke-width', 1.5)
        .attr('stroke-opacity', hasBrushes ? (withinBrush ? 0.3 : 0.03) : 0.3)
        .attr('class', 'pc-line')
        .style('cursor', 'pointer');
    });

    // Draw axes
    DIMENSIONS.forEach((dim) => {
      const xPos = xScale(dim.key);
      const axisG = g.append('g').attr('transform', `translate(${xPos}, 0)`);

      // Axis line
      axisG
        .append('line')
        .attr('y1', 0)
        .attr('y2', innerH)
        .attr('stroke', '#d1d5db')
        .attr('stroke-width', 1);

      // Ticks
      const scale = yScales[dim.key];
      const tickValues = scale.ticks(5);
      tickValues.forEach((tickVal) => {
        const yPos = scale(tickVal);
        axisG
          .append('line')
          .attr('x1', -4)
          .attr('x2', 4)
          .attr('y1', yPos)
          .attr('y2', yPos)
          .attr('stroke', '#d1d5db')
          .attr('stroke-width', 1);

        axisG
          .append('text')
          .attr('x', -8)
          .attr('y', yPos)
          .attr('text-anchor', 'end')
          .attr('dominant-baseline', 'middle')
          .attr('font-size', '9px')
          .attr('fill', '#9ca3af')
          .text(`$${Math.round(tickVal)}`);
      });

      // Axis label at top
      axisG
        .append('text')
        .attr('y', -12)
        .attr('text-anchor', 'middle')
        .attr('font-size', '11px')
        .attr('font-weight', 600)
        .attr('fill', '#374151')
        .text(dim.label);
    });

    // Hover overlay: invisible wider stroke paths for each line
    const overlayG = g.append('g').attr('class', 'overlays');

    visibleData.forEach((row) => {
      const points = [];
      DIMENSIONS.forEach((dim) => {
        const val = row[dim.key];
        if (val != null && typeof val === 'number') {
          points.push([xScale(dim.key), yScales[dim.key](val)]);
        }
      });

      if (points.length < 2) return;

      overlayG
        .append('path')
        .datum(row)
        .attr('d', lineGen(points))
        .attr('fill', 'none')
        .attr('stroke', 'transparent')
        .attr('stroke-width', 12)
        .style('cursor', 'pointer')
        .on('mouseenter', function (event, d) {
          // Highlight this line, dim others
          linesG.selectAll('.pc-line').attr('stroke-opacity', function (lineData) {
            return lineData === d ? 1.0 : 0.05;
          }).attr('stroke-width', function (lineData) {
            return lineData === d ? 3 : 1.5;
          });
        })
        .on('mousemove', function (event, d) {
          setTooltip({
            x: event.clientX + 14,
            y: event.clientY - 10,
            label: d.label || 'Unknown',
            state: d.state || '',
            pest365: d.pest365,
            pest365Plus: d.pest365Plus,
            oneTime: d.oneTime,
            initialServiceVisit: d.initialServiceVisit,
            mosquitoTickControl: d.mosquitoTickControl,
          });
        })
        .on('mouseleave', function () {
          const hasBrushes = Object.keys(brushExtents).length > 0;
          linesG.selectAll('.pc-line').attr('stroke-opacity', function (lineData) {
            if (hasBrushes) {
              return isWithinBrushes(lineData) ? 0.3 : 0.03;
            }
            return 0.3;
          }).attr('stroke-width', 1.5);
          setTooltip(null);
        });
    });

    // Brushes on each axis
    DIMENSIONS.forEach((dim) => {
      const xPos = xScale(dim.key);

      const brush = d3.brushY()
        .extent([[-12, 0], [12, innerH]])
        .on('brush end', function (event) {
          if (!event.sourceEvent) return; // only react to user events
          const selection = event.selection;
          setBrushExtents((prev) => {
            const next = { ...prev };
            if (selection) {
              next[dim.key] = selection;
            } else {
              delete next[dim.key];
            }
            return next;
          });
        });

      const brushG = g
        .append('g')
        .attr('class', `brush-${dim.key}`)
        .attr('transform', `translate(${xPos}, 0)`)
        .call(brush);

      // Style the brush selection rectangle
      brushG.selectAll('.selection')
        .attr('fill', '#3b82f6')
        .attr('fill-opacity', 0.15)
        .attr('stroke', '#3b82f6')
        .attr('stroke-width', 1);

      brushesRef.current[dim.key] = { brush, brushG };
    });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data, hiddenStates, stateColorScale, brushExtents]);

  const formatPrice = (val) => {
    if (val == null) return '--';
    if (val === 'not_available') return 'N/A';
    if (typeof val === 'number') return `$${val.toFixed(2)}`;
    return String(val);
  };

  if (!data || data.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>Multi-Dimension Price Profile</div>
        <div style={styles.subtitle}>Each line is an address crossing all price dimensions</div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No multi-dimension data available
        </div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.title}>Multi-Dimension Price Profile</div>
      <div style={styles.subtitle}>Each line is an address crossing all price dimensions</div>

      <div style={styles.controls}>
        <div style={styles.selectWrapper}>
          <span>Color by:</span>
          <select
            style={styles.select}
            value={colorBy}
            onChange={(e) => setColorBy(e.target.value)}
          >
            <option value="state">State</option>
          </select>
        </div>

        <div style={styles.pills}>
          {states.map((state) => {
            const hidden = hiddenStates.has(state);
            const color = stateColorScale(state);
            return (
              <span
                key={state}
                style={{
                  ...styles.pill,
                  opacity: hidden ? 0.4 : 1,
                  background: hidden ? '#f9fafb' : '#fff',
                }}
                onClick={() => toggleState(state)}
              >
                <span
                  style={{
                    ...styles.pillDot,
                    background: hidden ? '#d1d5db' : color,
                  }}
                />
                {state}
              </span>
            );
          })}
        </div>
      </div>

      <div style={styles.container} ref={containerRef}>
        <svg ref={svgRef} />
      </div>

      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {tooltip.label}
          </div>
          <div style={{ color: '#6b7280', marginBottom: '6px' }}>
            {tooltip.state}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Pest365</span>
            <span style={{ fontWeight: 600 }}>{formatPrice(tooltip.pest365)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Pest365+</span>
            <span style={{ fontWeight: 600 }}>{formatPrice(tooltip.pest365Plus)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>One-Time</span>
            <span style={{ fontWeight: 600 }}>{formatPrice(tooltip.oneTime)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Init. Visit</span>
            <span style={{ fontWeight: 600 }}>{formatPrice(tooltip.initialServiceVisit)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
            <span style={{ color: '#6b7280' }}>Mosquito/Tick</span>
            <span style={{ fontWeight: 600 }}>{formatPrice(tooltip.mosquitoTickControl)}</span>
          </div>
        </div>
      )}
    </div>
  );
}
